import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { NotificationProviderStatus } from '@/api/notificationProviders/notificationProvidersApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ToggleSwitch } from '@/components/ui/toggleSwitch';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  useConfigureEmailProviderMutation,
  useNotificationProvidersQuery,
  useRemoveEmailProviderMutation,
  useSendEmailProviderTestMutation,
} from '@/hooks/useNotificationProviders';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { isBlank, isValidEmail } from '@/utils/formValidation';
import { ProviderField } from './ProviderField';
import { ProviderStatusBadge } from './ProviderStatusBadge';
import { SmsProviderCard } from './SmsProviderCard';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { AssistantAskButton } from '@/components/assistant/AssistantAskButton';

const MINIMUM_TCP_PORT_NUMBER = 1;
const MAXIMUM_TCP_PORT_NUMBER = 65535;

function isValidSmtpPort(value: string): boolean {
  const trimmedValue = value.trim();
  if (trimmedValue.length === 0) {
    return false;
  }
  const parsedPort = Number(trimmedValue);
  return (
    Number.isInteger(parsedPort) &&
    parsedPort >= MINIMUM_TCP_PORT_NUMBER &&
    parsedPort <= MAXIMUM_TCP_PORT_NUMBER
  );
}

export function NotificationProvidersSection() {
  const { t } = useTranslation('notifications');
  const { t: translateAssistant } = useTranslation('assistant');
  const providersQuery = useNotificationProvidersQuery();

  const providers = providersQuery.data === undefined ? [] : providersQuery.data.providers;
  const emailStatus = providers.find(function isEmail(provider) {
    return provider.channel === 'email';
  });
  const smsStatus = providers.find(function isSms(provider) {
    return provider.channel === 'sms';
  });

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Send" />
        </span>
        <h2 className="flex-1 text-sm font-bold text-zooindigo">{t('providers.title')}</h2>
        <AssistantAskButton
          subject={t('providers.sms.title')}
          question={translateAssistant('askAbout.sms')}
        />
      </div>
      <p className="text-sm text-zoomuted mb-5">{t('providers.subtitle')}</p>

      {providersQuery.isLoading === true ? (
        <div className="py-8 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <AssistantTargetRegion targetKey={ASSISTANT_TARGET_KEY.integrationsEmailProvider}>
            <EmailProviderCard status={emailStatus} />
          </AssistantTargetRegion>
          <AssistantTargetRegion targetKey={ASSISTANT_TARGET_KEY.integrationsSmsProvider}>
            <SmsProviderCard status={smsStatus} />
          </AssistantTargetRegion>
        </div>
      )}
    </Card>
  );
}

interface EmailProviderCardProps {
  status: NotificationProviderStatus | undefined;
}

function EmailProviderCard({ status }: EmailProviderCardProps) {
  const { t } = useTranslation('notifications');
  const configureProvider = useConfigureEmailProviderMutation();
  const removeProvider = useRemoveEmailProviderMutation();
  const sendTest = useSendEmailProviderTestMutation();
  const [host, setHost] = useState('');
  const [port, setPort] = useState('587');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [useTls, setUseTls] = useState(true);
  const [fromName, setFromName] = useState('');
  const [fromAddress, setFromAddress] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [portError, setPortError] = useState('');
  const [fromAddressError, setFromAddressError] = useState('');
  const isConfigured = status !== undefined && status.isConfigured === true;

  function validateBeforeSave(): boolean {
    let isFormValid = true;
    if (isValidSmtpPort(port) === false) {
      setPortError(t('providers.validation.port'));
      isFormValid = false;
    } else {
      setPortError('');
    }
    if (isBlank(fromAddress) === true || isValidEmail(fromAddress) === false) {
      setFromAddressError(t('providers.validation.fromAddress'));
      isFormValid = false;
    } else {
      setFromAddressError('');
    }
    return isFormValid;
  }

  function save() {
    if (validateBeforeSave() === false) {
      return;
    }
    configureProvider.mutate(
      {
        host,
        port: Number(port),
        username,
        password,
        useTls,
        fromName,
        fromAddress,
      },
      {
        onSuccess: function reportSaved() {
          showSuccessNotification(t('providers.saved'));
        },
        onError: function reportError(error) {
          const message = resolveApiErrorMessage(error);
          showErrorNotification(message.length > 0 ? message : t('providers.saveError'));
        },
      }
    );
  }

  function remove() {
    removeProvider.mutate(undefined, {
      onSuccess: function reportRemoved() {
        showSuccessNotification(t('providers.removed'));
      },
    });
  }

  function test() {
    sendTest.mutate(testRecipient, {
      onSuccess: function reportResult(result) {
        if (result.delivered === true) {
          showSuccessNotification(t('providers.testSuccess'));
        } else {
          showErrorNotification(t('providers.testFailed'));
        }
      },
      onError: function reportError() {
        showErrorNotification(t('providers.testFailed'));
      },
    });
  }

  return (
    <div className="border border-zooborder rounded-xl p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="text-[13px] font-bold text-zooindigo">{t('providers.email.title')}</h3>
        <ProviderStatusBadge status={status} />
      </div>
      <p className="text-xs text-zoomuted mb-4">{t('providers.email.description')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ProviderField label={t('providers.email.host')}>
          <Input value={host} onChange={(event) => setHost(event.target.value)} />
        </ProviderField>
        <ProviderField label={t('providers.email.port')} error={portError}>
          <Input
            type="number"
            inputMode="numeric"
            min={MINIMUM_TCP_PORT_NUMBER}
            max={MAXIMUM_TCP_PORT_NUMBER}
            hasError={portError.length > 0}
            value={port}
            onChange={function updatePort(event) {
              setPort(event.target.value);
              setPortError('');
            }}
          />
        </ProviderField>
        <ProviderField label={t('providers.email.username')}>
          <Input value={username} onChange={(event) => setUsername(event.target.value)} />
        </ProviderField>
        <ProviderField label={t('providers.email.password')}>
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </ProviderField>
        <ProviderField label={t('providers.email.fromName')}>
          <Input value={fromName} onChange={(event) => setFromName(event.target.value)} />
        </ProviderField>
        <ProviderField label={t('providers.email.fromAddress')} error={fromAddressError}>
          <Input
            type="email"
            hasError={fromAddressError.length > 0}
            value={fromAddress}
            onChange={function updateFromAddress(event) {
              setFromAddress(event.target.value);
              setFromAddressError('');
            }}
          />
        </ProviderField>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <ToggleSwitch
          isChecked={useTls}
          accessibleLabel={t('providers.email.useTls')}
          onToggle={function toggle() {
            setUseTls(function invert(previous) {
              return previous === false;
            });
          }}
        />
        <span className="text-sm text-zoomuted">{t('providers.email.useTls')}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <Button onClick={save} isLoading={configureProvider.isPending}>
          {t('providers.save')}
        </Button>
        {isConfigured ? (
          <Button variant="outline" onClick={remove} isLoading={removeProvider.isPending}>
            {t('providers.remove')}
          </Button>
        ) : null}
      </div>

      <div className="mt-4 pt-4 border-t border-zooborder flex flex-col sm:flex-row sm:items-end gap-2">
        <div className="flex-1">
          <ProviderField label={t('providers.email.testRecipient')}>
            <Input
              type="email"
              value={testRecipient}
              onChange={(event) => setTestRecipient(event.target.value)}
            />
          </ProviderField>
        </div>
        <Button
          variant="outline"
          onClick={test}
          disabled={testRecipient.length === 0}
          isLoading={sendTest.isPending}
        >
          {t('providers.test')}
        </Button>
      </div>
    </div>
  );
}
